// Compact Stoppuhr-Steuerung für den Kalender-Header. Spiegelt die Funktionen
// der /timer-Seite (TimerHero), aber als kleines Popover-Widget statt
// vollformatiger Hero-Section.
//
// - <CalendarTimerButton/>: bleibt persistent in der Toolbar. Bei laufendem
//   Timer zeigt der Button die Live-Elapsed-Zeit (1s-Tick), sonst nur das
//   Icon. Klick toggelt das Popover.
// - <CalendarTimerPopover/>: enthält je nach State entweder die Start-Form
//   (Beschreibung, Projekt, Tätigkeit, Tags) oder die Running-Anzeige
//   (Elapsed + Description + Stop-Button).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, Pause, Play, Timer as TimerIcon, X } from 'lucide-react'
import { type FormEvent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { api, type Tag, type TimeEntry } from '../lib/api'
import { invalidateEntryData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { shouldDismissOnOutsidePointer } from '../lib/outside-click'
import { getRecentProjects, pushRecentProject } from '../lib/recent-projects'
import { resolveSuggestionFill } from '../lib/suggestion-fill'
import { useDragToClose } from '../lib/use-drag-to-close'
import { useIsMobile } from '../lib/use-media-query'
import { useFocusScrollIntoView, useSheetMaxHeight } from '../lib/use-visual-viewport'
import { cn, formatClock, formatTime } from '../lib/utils'
import { DescriptionAutocomplete } from './description-autocomplete'
import { TagPicker } from './tag-picker'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Select } from './ui/select'

/**
 * Laufender Timer per dediziertem /running-Endpoint — unabhängig von jedem
 * Datums-Fenster. Die frühere Heute-Liste nutzte den UTC-Tag als from-Filter:
 * ein Timer, der vor der UTC-Mitternacht gestartet wurde (z.B. 00:44 lokal),
 * fiel nach 02:00 lokal aus dem Fenster — Button und Sheet zeigten das
 * Start-Formular, obwohl der Timer weiterlief (D3).
 *
 * Geteilt zwischen dem Toolbar-Button (Desktop) und dem Stoppuhr-Slot der
 * mobilen Shell-Bar (calendar-mobile-slots.tsx); gleicher queryKey →
 * React-Query dedupliziert Fetch + Interval.
 */
export function useRunningEntry(): TimeEntry | null {
  const { data } = useQuery({
    queryKey: ['entries', 'running'],
    queryFn: () => api.runningEntry(),
    refetchInterval: 60_000,
  })
  return data?.entry ?? null
}

export function CalendarTimerButton() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Am Phone rendert die Stoppuhr als Portal-Bottom-Sheet statt als
  // Anchor-Popover (das ragte am Phone bei x=−228 links aus dem Viewport).
  const isMobile = useIsMobile()

  const running = useRunningEntry()

  // Live-Tick nur wenn ein Timer läuft.
  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [running])

  // Outside-Click + Esc schließen. Outside-Click nur im Desktop-Popover —
  // das Mobile-Sheet lebt als Portal AUSSERHALB von containerRef (jeder Tap
  // darin würde sonst schließen); dort übernimmt der Backdrop das Dismiss.
  // Gleiches Portal-Problem hat das Autocomplete-Dropdown im StartPanel
  // (hängt an document.body): ohne Ausnahme schließt pointerdown das Popover,
  // bevor der click den Vorschlag übernimmt — daher der Exempt-Check.
  useEffect(() => {
    if (!open) return
    function onPointer(e: PointerEvent) {
      const el = e.target as HTMLElement | null
      if (shouldDismissOnOutsidePointer(el, containerRef.current)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (!isMobile) window.addEventListener('pointerdown', onPointer, true)
    window.addEventListener('keydown', onKey)
    return () => {
      if (!isMobile) window.removeEventListener('pointerdown', onPointer, true)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, isMobile])

  const elapsed = running ? now - new Date(running.startTime).getTime() : 0

  // Entprellung gegen den Toggle-Race beim ersten Tap (D9): Auf Touch kann
  // ein Tap als pointerdown+click-Paar doppelt durchschlagen bzw. ein
  // schneller Folge-Tap das gerade geöffnete Sheet sofort wieder schließen.
  // Der Trigger liegt zwar im Outside-Click-Excluded-Bereich (containerRef),
  // aber ein doppelt gefeuerter click toggelt trotzdem zweimal — daher
  // Toggles innerhalb von 350 ms nach dem letzten ignorieren.
  const lastToggleAt = useRef(0)
  function toggleOpen() {
    const t = Date.now()
    if (t - lastToggleAt.current < 350) return
    lastToggleAt.current = t
    setOpen((v) => !v)
  }

  function onChanged() {
    invalidateEntryData(queryClient)
    queryClient.invalidateQueries({ queryKey: ['entries', 'running'] })
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={running ? 'Stoppuhr läuft — Klick zum Stoppen' : 'Stoppuhr starten'}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 touch:h-11 touch:px-3.5 text-xs transition-colors shrink-0',
          running
            ? 'border-accent/40 bg-accent/10 text-accent hover:bg-accent/15'
            : 'border-border-subtle bg-bg-elevated/60 text-text-secondary hover:text-text-primary',
        )}
      >
        {running ? (
          <>
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
            </span>
            <span className="font-mono tabular-nums">{formatClock(elapsed)}</span>
          </>
        ) : (
          <>
            <TimerIcon className="h-3.5 w-3.5" />
            {/* Mobil icon-only (Zeile 2 des Kalender-Kopfs ist eng) — die
                laufende Zeit oben bleibt dagegen IMMER sichtbar. */}
            <span className="hidden sm:inline">Stoppuhr</span>
          </>
        )}
        {/* Chevron nur ≥sm — mobil zählt jeder Pixel in Zeile 2 des
            Kalender-Kopfs, das Sheet öffnet auch ohne Dropdown-Hinweis. */}
        <ChevronDown className="hidden sm:block h-3 w-3 opacity-60" />
      </button>
      {open && (
        <CalendarTimerPopover
          running={running}
          onChanged={onChanged}
          onClose={() => setOpen(false)}
          elapsed={elapsed}
        />
      )}
    </div>
  )
}

export function CalendarTimerPopover({
  running,
  onChanged,
  onClose,
  elapsed,
  sheet = false,
}: {
  running: TimeEntry | null
  onChanged: () => void
  onClose: () => void
  elapsed: number
  /** Erzwingt das Portal-Bottom-Sheet unabhängig vom Phone-Match — für
   *  Mounts aus der Bottom-Bar (calendar-mobile-slots), die im Tablet-Band
   *  KEINEN Anker haben: der Desktop-Zweig positioniert `absolute top-full`
   *  relativ zum `relative`-Container des CalendarTimerButton. */
  sheet?: boolean
}) {
  // Escape schließt das Sheet — hier im Popover selbst, damit es für JEDEN
  // Mount gilt (Top-Bar-Trigger UND Bottom-Bar); der Handler am Trigger-
  // Button griff beim Bottom-Bar-Mount nicht (E2E-Fund).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects(),
  })
  const { data: servicesData } = useQuery({
    queryKey: ['services', { archived: false }],
    queryFn: () => api.listServices(),
  })
  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(),
  })
  const { data: tagsData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.listTags(),
  })
  const projects = projectsData?.projects ?? []
  const services = servicesData?.services ?? []
  const customers = customersData?.customers ?? []
  const tags = tagsData?.tags ?? []
  const customerMap = new Map(customers.map((c) => [c.id, c]))

  // Sheet-Rendering am Phone (kein Platz für Anker-Popover) ODER erzwungen
  // per Prop (Bar-Mount im Tablet-Band). Am Phone ist sheet||isMobile
  // identisch zu vorher — Drag-to-close + Fokus-Scroll bleiben gleich.
  const isMobile = useIsMobile()
  const asSheet = sheet || isMobile
  const sheetMaxHeight = useSheetMaxHeight()
  const drag = useDragToClose(onClose, asSheet)
  const bodyRef = useFocusScrollIntoView<HTMLDivElement>(asSheet)

  // Desktop-Popover: defensives Clamping gegen den linken Viewport-Rand.
  // Der Anker (right-0 am Trigger) kann das 360px-Panel bei schmalen
  // Fenstern links aus dem Viewport schieben — dann nach rechts shiften.
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const [shiftX, setShiftX] = useState(0)
  useLayoutEffect(() => {
    if (asSheet) return
    const el = popoverRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const margin = 12
    if (rect.left < margin) {
      // Nie weiter shiften, als der rechte Rand Platz lässt.
      setShiftX(Math.min(margin - rect.left, window.innerWidth - margin - rect.right))
    } else {
      setShiftX(0)
    }
  }, [asSheet])

  const panels = running ? (
    <RunningPanel
      running={running}
      elapsed={elapsed}
      projects={projects}
      services={services}
      tags={tags}
      customerMap={customerMap}
      onStop={() => {
        onChanged()
        onClose()
      }}
      onEdited={onChanged}
    />
  ) : (
    <StartPanel
      projects={projects}
      services={services}
      tags={tags}
      customerMap={customerMap}
      onStarted={() => {
        onChanged()
        // Bleibt offen — User sieht direkt die laufende Anzeige.
      }}
    />
  )

  // ── Sheet-Modus (Phone bzw. Bar-Mount im Band): Portal-Bottom-Sheet
  //    (Muster wie dialog.tsx) ──────────
  if (asSheet) {
    if (typeof document === 'undefined') return null
    return createPortal(
      <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Stoppuhr">
        <button
          type="button"
          className="absolute inset-0 z-0 bg-bg-base/70 backdrop-blur-sm animate-fade-in"
          aria-label="Schließen"
          onClick={onClose}
        />
        <div
          className="app-safe-inline absolute bottom-0 left-0 right-0 z-10 flex flex-col bg-bg-elevated rounded-t-2xl border-t border-border-subtle shadow-2xl animate-slide-up"
          style={{ ...drag.sheetStyle, maxHeight: sheetMaxHeight }}
        >
          {/* Drag-Handle — drag-down-to-close */}
          <div
            className="flex justify-center pt-2 pb-1 shrink-0 cursor-grab active:cursor-grabbing"
            {...drag.handlers}
          >
            <div className="w-10 h-1 rounded-full bg-text-tertiary/40" />
          </div>
          <div
            className="flex shrink-0 items-center justify-between gap-3 px-4 pb-1"
            {...drag.handlers}
          >
            <p className="text-xs uppercase tracking-wider text-text-tertiary">Stoppuhr</p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Schließen"
              className="shrink-0 -mr-1 p-2.5 rounded-md text-text-tertiary hover:text-text-primary hover:bg-bg-overlay/60 touch:opacity-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div
            ref={bodyRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
            style={{ paddingBottom: 'var(--app-safe-bottom)' }}
          >
            {panels}
          </div>
        </div>
      </div>,
      document.body,
    )
  }

  // ── Desktop/Tablet: Anchor-Popover (Verhalten wie bisher, plus
  //    Clamping: max-w, max-h + Scroll statt overflow-hidden) ──────
  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Stoppuhr"
      className="absolute right-0 top-full mt-2 z-30 w-[min(360px,90vw)] max-w-[calc(100vw-24px)] max-h-[min(70vh,560px)] overflow-y-auto rounded-xl border border-border-subtle bg-bg-elevated shadow-xl animate-fade-in"
      style={shiftX !== 0 ? { transform: `translateX(${shiftX}px)` } : undefined}
    >
      {panels}
    </div>
  )
}

function RunningPanel({
  running,
  elapsed,
  projects,
  services,
  tags,
  customerMap,
  onStop,
  onEdited,
}: {
  running: TimeEntry
  elapsed: number
  projects: { id: string; name: string; customerId: string }[]
  services: { id: string; name: string }[]
  tags: Tag[]
  customerMap: Map<string, { name: string }>
  /** Timer gestoppt — Popover darf schließen. */
  onStop: () => void
  /** Inhalt aktualisiert — Queries refetchen, Popover bleibt offen. */
  onEdited: () => void
}) {
  // Lokaler Edit-Buffer für den laufenden Eintrag.
  const [desc, setDesc] = useState(running.description)
  const [editingDesc, setEditingDesc] = useState(false)
  const [notes, setNotes] = useState(running.notes ?? '')
  const [editingNotes, setEditingNotes] = useState(false)
  const [projectId, setProjectId] = useState(running.projectId ?? '')
  const [serviceId, setServiceId] = useState(running.serviceId ?? '')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(running.tagIds ?? [])
  // Beschreibung NUR resyncen, solange NICHT getippt wird (kein Fokus) — so wird
  // ein Refetch/anderer Tab idle korrekt übernommen, ohne aktives Tippen oder den
  // live tickenden Timer das Feld überschreiben zu lassen.
  useEffect(() => {
    if (!editingDesc) setDesc(running.description)
  }, [running.description, editingDesc])
  useEffect(() => {
    if (!editingNotes) setNotes(running.notes ?? '')
  }, [running.notes, editingNotes])
  // Projekt/Tätigkeit/Tags speichern sofort -> lokal == Server; bei externem
  // Wechsel (anderer Tab) frisch ziehen. Kein Tipp-Buffer, daher unkritisch.
  useEffect(() => {
    setProjectId(running.projectId ?? '')
    setServiceId(running.serviceId ?? '')
    setSelectedTagIds(running.tagIds ?? [])
  }, [running.projectId, running.serviceId, running.tagIds])

  const updateMutation = useMutation({
    mutationFn: (patch: {
      description?: string
      projectId?: string | null
      serviceId?: string | null
      tagIds?: string[]
      notes?: string | null
    }) => api.updateEntry(running.id, patch),
    onSuccess: () => onEdited(),
    onError: (err: Error) => notify.error(err.message),
  })

  const stopMutation = useMutation({
    mutationFn: (id: string) => api.stopEntry(id),
    onSuccess: () => {
      onStop()
      notify.success('Timer gestoppt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function saveDescription() {
    setEditingDesc(false)
    const next = desc.trim()
    if (next === running.description) return
    if (!next) {
      // Leere Beschreibung nicht speichern; der Resync (editingDesc=false) stellt
      // den letzten gültigen Server-Stand wieder her — kein harter Daten-Reset hier.
      notify.error('Beschreibung darf nicht leer sein')
      return
    }
    updateMutation.mutate({ description: next })
  }

  function saveNotes() {
    setEditingNotes(false)
    // Leere Notiz ist gültig (→ null), damit sie auch wieder entfernt werden kann.
    const next = notes.trim() || null
    if (next === (running.notes ?? null)) return
    updateMutation.mutate({ notes: next })
  }

  function changeProject(value: string) {
    setProjectId(value)
    updateMutation.mutate({ projectId: value || null })
  }
  function changeService(value: string) {
    setServiceId(value)
    updateMutation.mutate({ serviceId: value || null })
  }
  function toggleTag(id: string) {
    const next = selectedTagIds.includes(id)
      ? selectedTagIds.filter((x) => x !== id)
      : [...selectedTagIds, id]
    setSelectedTagIds(next)
    updateMutation.mutate({ tagIds: next })
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center gap-2 text-2xs uppercase tracking-[0.18em] text-accent">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-accent" />
        </span>
        läuft seit {formatTime(running.startTime)}
      </div>
      <p className="font-mono tabular-nums text-4xl font-light leading-none">
        {formatClock(elapsed)}
      </p>
      {/* Beschreibung tagsüber befüllbar — speichert beim Verlassen des Feldes. */}
      <Input
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        onFocus={() => setEditingDesc(true)}
        onBlur={saveDescription}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
        }}
        aria-label="Beschreibung"
        placeholder="Beschreibung ergänzen…"
        className="h-10"
      />
      <div className="grid grid-cols-2 gap-2">
        <Select
          value={projectId}
          onChange={(e) => changeProject(e.target.value)}
          aria-label="Projekt"
          className="h-9 text-xs"
        >
          <option value="">— Projekt —</option>
          {projects.map((p) => {
            const cust = customerMap.get(p.customerId)
            return (
              <option key={p.id} value={p.id}>
                {cust ? `${cust.name} – ` : ''}
                {p.name}
              </option>
            )
          })}
        </Select>
        <Select
          value={serviceId}
          onChange={(e) => changeService(e.target.value)}
          aria-label="Tätigkeit"
          className="h-9 text-xs"
        >
          <option value="">— Tätigkeit —</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>
      {tags.length > 0 && (
        <TagPicker tags={tags} selectedIds={selectedTagIds} onToggle={toggleTag} />
      )}
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        onFocus={() => setEditingNotes(true)}
        onBlur={saveNotes}
        aria-label="Notizen"
        rows={2}
        placeholder="Notizen (optional)…"
        className="flex w-full rounded-md border border-border-default bg-bg-overlay px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30"
      />
      <Button
        variant="danger"
        onClick={() => stopMutation.mutate(running.id)}
        disabled={stopMutation.isPending}
        className="w-full"
      >
        <Pause className="h-4 w-4" />
        Timer stoppen
      </Button>
    </div>
  )
}

function StartPanel({
  projects,
  services,
  tags,
  customerMap,
  onStarted,
}: {
  projects: { id: string; name: string; customerId: string }[]
  services: { id: string; name: string }[]
  tags: Tag[]
  customerMap: Map<string, { name: string }>
  onStarted: () => void
}) {
  const [description, setDescription] = useState('')
  const [notes, setNotes] = useState('')
  const [projectId, setProjectId] = useState<string>('')
  const [serviceId, setServiceId] = useState<string>('')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [recentIds, setRecentIds] = useState<string[]>([])
  const queryClient = useQueryClient()

  useEffect(() => {
    setRecentIds(getRecentProjects())
  }, [])

  // Vorbelegung "zuletzt genutzt": Projekt + Tätigkeit der jüngsten Buchung
  // vorauswählen, damit die Stammbuchung (90-99% derselbe Schlüssel) nur noch
  // eine Beschreibung + Start braucht. Greift einmalig beim Öffnen und nur auf
  // noch leere Felder, sodass eine manuelle Auswahl nie überschrieben wird.
  const lastSelection = useQuery({
    queryKey: ['time-entries', 'last-selection'],
    queryFn: () => api.getLastSelection(),
    staleTime: 60_000,
  })
  const prefillDone = useRef(false)
  useEffect(() => {
    if (prefillDone.current) return
    const sel = lastSelection.data
    if (!sel) return
    // Erst validieren, wenn die Listen geladen sind — sonst würde eine gültige
    // ID gegen eine noch leere (ladende) Liste verworfen.
    if (sel.projectId && projects.length === 0) return
    if (sel.serviceId && services.length === 0) return
    // Archivierte/gelöschte Einträge sind nicht in den Listen -> auf '' fallen.
    const validProject =
      sel.projectId && projects.some((p) => p.id === sel.projectId) ? sel.projectId : ''
    const validService =
      sel.serviceId && services.some((s) => s.id === sel.serviceId) ? sel.serviceId : ''
    setProjectId((cur) => (cur === '' ? validProject : cur))
    setServiceId((cur) => (cur === '' ? validService : cur))
    prefillDone.current = true
  }, [lastSelection.data, projects, services])

  const startMutation = useMutation({
    mutationFn: () =>
      api.startEntry({
        description: description.trim(),
        projectId: projectId || null,
        serviceId: serviceId || null,
        tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      if (projectId) {
        pushRecentProject(projectId)
        setRecentIds(getRecentProjects())
      }
      // Die gerade gestartete Buchung ist jetzt die "zuletzt genutzte".
      queryClient.invalidateQueries({ queryKey: ['time-entries', 'last-selection'] })
      setDescription('')
      setNotes('')
      setSelectedTagIds([])
      onStarted()
      notify.success('Timer gestartet')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!description.trim()) {
      notify.error('Bitte eine Beschreibung eingeben')
      return
    }
    startMutation.mutate()
  }

  const recentProjects = recentIds
    .map((id) => projects.find((p) => p.id === id))
    .filter((p): p is (typeof projects)[number] => !!p)
    .slice(0, 5)

  return (
    <form onSubmit={onSubmit} className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wider text-text-tertiary">Stoppuhr starten</p>
      </div>
      <DescriptionAutocomplete
        placeholder="z.B. Ember — UI Polish"
        value={description}
        onChange={setDescription}
        onPick={(s) => {
          setDescription(s.description)
          // Projekt/Tätigkeit der letzten Verwendung NUR in noch leere
          // Felder übernehmen — eine manuelle Auswahl bleibt unangetastet.
          const fill = resolveSuggestionFill(s, { projectId, serviceId }, projects, services)
          if (fill.projectId) setProjectId(fill.projectId)
          if (fill.serviceId) setServiceId(fill.serviceId)
        }}
        projects={projects}
        services={services}
        aria-label="Beschreibung"
        className="h-10"
        autoFocus
      />
      {recentProjects.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-2xs uppercase tracking-wider text-text-tertiary">Zuletzt:</span>
          {recentProjects.map((p) => {
            const cust = customerMap.get(p.customerId)
            const active = projectId === p.id
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setProjectId(active ? '' : p.id)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                  active
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-border-default text-text-secondary hover:border-accent/40 hover:text-text-primary',
                )}
              >
                {cust?.name ?? '—'} · {p.name}
              </button>
            )
          })}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Select
          value={projectId}
          onChange={(e) => setProjectId(e.target.value)}
          aria-label="Projekt"
          className="h-9 text-xs"
        >
          <option value="">— Projekt —</option>
          {projects.map((p) => {
            const cust = customerMap.get(p.customerId)
            return (
              <option key={p.id} value={p.id}>
                {cust ? `${cust.name} – ` : ''}
                {p.name}
              </option>
            )
          })}
        </Select>
        <Select
          value={serviceId}
          onChange={(e) => setServiceId(e.target.value)}
          aria-label="Tätigkeit"
          className="h-9 text-xs"
        >
          <option value="">— Tätigkeit —</option>
          {services.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </div>
      {tags.length > 0 && (
        <TagPicker
          tags={tags}
          selectedIds={selectedTagIds}
          onToggle={(id) =>
            setSelectedTagIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
            )
          }
        />
      )}
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        aria-label="Notizen"
        rows={2}
        placeholder="Notizen (optional)…"
        className="flex w-full rounded-md border border-border-default bg-bg-overlay px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30"
      />
      <Button type="submit" disabled={startMutation.isPending} className="w-full">
        <Play className="h-4 w-4" fill="currentColor" />
        Start
      </Button>
    </form>
  )
}
